// グラフタイプと測定尺度の互換性ユーティリティ

import { ColumnSchema } from '../../types/core.types';

export interface GraphTypeAvailability {
  value: string;
  label: string;
  description?: string;
  available: boolean;
  reason?: string;  // 利用不可の理由
}

/**
 * グラフタイプと測定尺度の互換性をチェック
 * @param graphType グラフタイプ
 * @param xColumn X軸の列（またはヒストグラムの場合は対象列）
 * @param yColumn Y軸の列（任意）
 * @returns 利用可能かどうかと理由
 */
export function checkGraphTypeCompatibility(
  graphType: string,
  xColumn?: ColumnSchema,
  yColumn?: ColumnSchema
): { available: boolean; reason?: string } {
  const xScale = xColumn?.scale?.scale;
  const yScale = yColumn?.scale?.scale;
  const xType = xColumn?.type;
  const yType = yColumn?.type;


  switch (graphType) {
    case 'histogram':
      // ヒストグラム: interval/ratio尺度の数値型のみ
      if (!xColumn) {
        return { available: true }; // 列が未選択の場合は選択可能
      }
      if (xType !== 'int64' && xType !== 'float64') {
        return { available: false, reason: 'Histogram requires numeric data' };
      }
      if (xScale === 'nominal' || xScale === 'ordinal') {
        return { 
          available: false, 
          reason: 'Histogram requires interval or ratio scale (not categorical)' 
        };
      }
      return { available: true };

    case 'scatter':
      // 散布図: 両軸がinterval/ratio尺度の数値型
      if (!xColumn || !yColumn) {
        return { available: true }; // 列が未選択の場合は選択可能
      }
      
      // 両軸が数値型であることを確認
      if ((xType !== 'int64' && xType !== 'float64') || 
          (yType !== 'int64' && yType !== 'float64')) {
        return { available: false, reason: 'Scatter plot requires numeric data on both axes' };
      }
      
      // 両軸がinterval/ratio尺度であることを確認
      if (xScale === 'nominal' || xScale === 'ordinal') {
        return { 
          available: false, 
          reason: 'X-axis requires interval or ratio scale for scatter plot' 
        };
      }
      if (yScale === 'nominal' || yScale === 'ordinal') {
        return { 
          available: false, 
          reason: 'Y-axis requires interval or ratio scale for scatter plot' 
        };
      }
      return { available: true };

    case 'bar': {
      // 棒グラフ: X軸がnominal/ordinal、Y軸がinterval/ratio
      if (!xColumn) {
        return { available: true }; // 列が未選択の場合は選択可能
      }
      
      // X軸はカテゴリカル（文字列）またはnominal/ordinal尺度の数値
      const xIsCategorical = xType === 'string' || xType === 'boolean' ||
        (xScale && (xScale === 'nominal' || xScale === 'ordinal'));
      
      if (!xIsCategorical) {
        return { 
          available: false, 
          reason: 'Bar chart requires categorical data (nominal/ordinal) on X-axis' 
        };
      }
      
      // Y軸が指定されている場合は、interval/ratio尺度の数値型であることを確認
      if (yColumn) {
        if (yType !== 'int64' && yType !== 'float64') {
          return { available: false, reason: 'Y-axis requires numeric data' };
        }
        if (yScale === 'nominal' || yScale === 'ordinal') {
          return { 
            available: false, 
            reason: 'Y-axis requires interval or ratio scale for aggregation' 
          };
        }
      }
      
      return { available: true };
    }

    case 'timeseries':
      // 時系列: X軸が日時型、Y軸がinterval/ratio尺度の数値型
      if (!xColumn) {
        return { available: true }; // 列が未選択の場合は選択可能
      }
      
      // X軸が日時型であることを確認
      if (xType !== 'date' && xType !== 'datetime') {
        return { available: false, reason: 'Time series requires date/datetime on X-axis' };
      }
      
      // Y軸が指定されている場合は、interval/ratio尺度の数値型であることを確認
      if (yColumn) {
        if (yType !== 'int64' && yType !== 'float64') {
          return { available: false, reason: 'Y-axis requires numeric data' };
        }
        if (yScale === 'nominal' || yScale === 'ordinal') {
          return { 
            available: false, 
            reason: 'Y-axis requires interval or ratio scale for time series' 
          };
        }
      }
      
      return { available: true };

    default:
      // 未知のグラフタイプは常に利用可能とする
      return { available: true };
  }
}

/**
 * 利用可能なグラフタイプのリストを取得
 * @param graphTypes 全グラフタイプのリスト
 * @param xColumn X軸の列
 * @param yColumn Y軸の列
 * @returns フィルタリングされたグラフタイプのリスト
 */
export function getAvailableGraphTypes(
  graphTypes: Array<{ value: string; label: string; description?: string }>,
  xColumn?: ColumnSchema,
  yColumn?: ColumnSchema
): GraphTypeAvailability[] {
  return graphTypes.map(type => {
    const compatibility = checkGraphTypeCompatibility(type.value, xColumn, yColumn);
    return {
      ...type,
      available: compatibility.available,
      reason: compatibility.reason
    };
  });
}

/**
 * グラフタイプに適した列を推奨
 * @param graphType グラフタイプ
 * @param columns 利用可能な列のリスト
 * @returns 推奨される列の組み合わせ
 */
export function recommendColumnsForGraphType(
  graphType: string,
  columns: ColumnSchema[]
): {
  xColumnSuggestions: ColumnSchema[];
  yColumnSuggestions: ColumnSchema[];
  message?: string;
} {
  const numericColumns = columns.filter(col => 
    (col.type === 'int64' || col.type === 'float64')
  );
  
  const intervalRatioColumns = numericColumns.filter(col => {
    const scale = col.scale?.scale;
    return !scale || scale === 'interval' || scale === 'ratio';
  });
  
  const categoricalColumns = columns.filter(col => {
    const scale = col.scale?.scale;
    return col.type === 'string' || col.type === 'boolean' ||
      scale === 'nominal' || scale === 'ordinal';
  });
  
  const dateTimeColumns = columns.filter(col => 
    col.type === 'date' || col.type === 'datetime'
  );

  switch (graphType) {
    case 'histogram':
      return {
        xColumnSuggestions: intervalRatioColumns,
        yColumnSuggestions: [],
        message: intervalRatioColumns.length === 0 
          ? 'No numeric columns with interval/ratio scale available for histogram'
          : undefined
      };

    case 'scatter':
      return {
        xColumnSuggestions: intervalRatioColumns,
        yColumnSuggestions: intervalRatioColumns,
        message: intervalRatioColumns.length < 2
          ? 'Need at least 2 numeric columns with interval/ratio scale for scatter plot'
          : undefined
      };

    case 'bar':
      return {
        xColumnSuggestions: categoricalColumns,
        yColumnSuggestions: intervalRatioColumns,
        message: categoricalColumns.length === 0
          ? 'No categorical columns available for bar chart X-axis'
          : intervalRatioColumns.length === 0
          ? 'No numeric columns with interval/ratio scale available for Y-axis'
          : undefined
      };

    case 'timeseries':
      return {
        xColumnSuggestions: dateTimeColumns,
        yColumnSuggestions: intervalRatioColumns,
        message: dateTimeColumns.length === 0
          ? 'No date/datetime columns available for time series'
          : intervalRatioColumns.length === 0
          ? 'No numeric columns with interval/ratio scale available for Y-axis'
          : undefined
      };

    default:
      return {
        xColumnSuggestions: columns,
        yColumnSuggestions: columns
      };
  }
}